import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, STATUS_LABEL } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  ShoppingBag, Users, Wallet, AlarmClock, ArrowRight, TrendingUp,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, gold, testid }) => (
  <div
    className={`bg-white border ${gold ? "border-[#B89968]/40" : "border-[#E5DCC7]"} p-6 flex items-start justify-between relative overflow-hidden`}
    data-testid={testid}
  >
    {gold && (
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#B89968] via-[#D9C39A] to-transparent" />
    )}
    <div>
      <div className="clf-label mb-3">{label}</div>
      <div className={`text-3xl font-semibold tracking-tight font-mono ${accent || "text-stone-900"}`}>
        {value}
      </div>
    </div>
    <div className={`h-10 w-10 ${gold ? "bg-[#B89968]/10 border-[#B89968]/30" : "bg-stone-50 border-[#E5DCC7]"} border flex items-center justify-center`}>
      <Icon className={`h-4 w-4 ${gold ? "text-[#8E7144]" : "text-stone-700"}`} strokeWidth={1.5} />
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => console.error("Failed to fetch stats:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-4 lg:p-10 text-stone-500" data-testid="dashboard-loading">
        Memuat...
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-4 lg:p-10 text-red-500" data-testid="dashboard-error">
        Gagal memuat data dashboard. Pastikan server backend berjalan.
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-10 space-y-8 max-w-[1400px]" data-testid="dashboard-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <div className="clf-label mb-2 clf-gold-text">Ringkasan</div>
          <h1 className="text-3xl tracking-tight font-semibold">Dashboard</h1>
          <p className="text-sm text-stone-500 mt-1">
            Pantau pesanan, pendapatan, dan tenggat waktu CLF.
          </p>
        </div>
        <Link
          to="/orders/new"
          data-testid="dashboard-new-order-btn"
          className="bg-[#16202B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#243345] transition-colors"
        >
          + Order Baru
        </Link>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={ShoppingBag} label="Total Order"
          value={stats.total_orders} testid="stat-total-orders"
        />
        <StatCard
          icon={Users} label="Total Customer"
          value={stats.total_customers} testid="stat-total-customers"
        />
        <StatCard
          icon={Wallet} label="Total Dibayar"
          value={formatRupiah(stats.total_revenue)}
          accent="text-[#8E7144]" gold testid="stat-revenue"
        />
        <StatCard
          icon={AlarmClock} label="Belum Dibayar"
          value={formatRupiah(stats.pending_amount)}
          accent="text-[#A63D30]" testid="stat-pending"
        />
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {["masuk", "proses", "selesai", "diterima"].map((s) => (
          <Link
            key={s}
            to={`/orders?status=${s}`}
            data-testid={`status-card-${s}`}
            className="bg-white border border-[#E5DCC7] p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <div className="clf-label mb-3">{STATUS_LABEL[s]}</div>
            <div className="flex items-end justify-between">
              <div className="text-4xl font-medium font-mono tracking-tight">
                {stats.by_status[s]}
              </div>
              <ArrowRight className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue chart */}
        <div className="lg:col-span-2 bg-white border border-[#E5DCC7] p-6" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="clf-label mb-1">Pendapatan</div>
              <h2 className="text-lg font-medium">6 bulan terakhir</h2>
            </div>
            <TrendingUp className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
          </div>
          <div className="h-64 w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenue_by_month} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="label" stroke="#78716C" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#78716C" fontSize={11} tickLine={false} axisLine={false}
                  tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v}
                />
                <Tooltip
                  formatter={(v) => formatRupiah(v)}
                  contentStyle={{ background: "#16202B", border: "none", color: "#fff", fontSize: 12 }}
                  cursor={{ fill: "rgba(28,43,54,0.05)" }}
                />
                <Bar dataKey="revenue" fill="#B89968" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Deadline alerts */}
        <div className="bg-white border border-[#E5DCC7] p-6" data-testid="deadline-alerts">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="clf-label mb-1">Pengingat</div>
              <h2 className="text-lg font-medium">Tenggat Dekat</h2>
            </div>
            <AlarmClock className="h-4 w-4 text-[#A63D30]" strokeWidth={1.5} />
          </div>
          {stats.deadline_alerts.length === 0 ? (
            <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-[#E5DCC7]">
              Tidak ada tenggat dekat
            </div>
          ) : (
            <ul className="space-y-3">
              {stats.deadline_alerts.slice(0, 5).map((a) => (
                <li key={a.order_id} className="flex items-center justify-between border-b border-stone-100 pb-3 last:border-0">
                  <div>
                    <Link
                      to={`/orders/${a.order_id}`}
                      className="text-sm font-mono font-medium hover:text-[#A63D30]"
                      data-testid={`alert-${a.order_id}`}
                    >
                      {a.order_number}
                    </Link>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      Status: {STATUS_LABEL[a.status]}
                    </div>
                  </div>
                  <div className={`text-xs font-medium ${a.days_left < 0 ? "text-red-600" : a.days_left <= 1 ? "text-[#A63D30]" : "text-amber-700"}`}>
                    {a.days_left < 0 ? `${Math.abs(a.days_left)} hari telat` : a.days_left === 0 ? "Hari ini" : `${a.days_left} hari lagi`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
